/**
 * The Reach Wire panel's view-model (M4 spec §12.1, §15 item 2): the filters (All, This system, Nearby, My cargo;
 * M9: Disabled hulls, CH-14b's claimable-hulk lines) and one row per item with its day stamp, severity (a word and a glyph, never colour alone) and cause.
 * H6 (D-617 spec §6): rows name their system (never the raw id) and drop the cause line when the headline already says it.
 * UI events only.
 */
import type { StoryWireOutlet, WireItem } from '../../strategic/types'
import { isDisabledHullHeadline } from '../../strategic/wire/disabledHullLine'
import outletsJson from './wireOutlets.json'

export const WIRE_FILTERS = ['all', 'system', 'nearby', 'cargo', 'hulls'] as const
export type WireFilter = (typeof WIRE_FILTERS)[number]

export const WIRE_FILTER_LABELS: Readonly<Record<WireFilter, string>> = Object.freeze({
  all: 'All',
  system: 'This system',
  nearby: 'Nearby',
  cargo: 'My cargo',
  hulls: 'Disabled hulls',
})

/** Where the player is and what they carry. */
export interface WireFilterContext {
  /** The player's system (null: between systems or at the title). */
  systemId: string | null
  /** Systems one relay from the player's. */
  neighbours: ReadonlySet<string>
  /** Wares in the hold. */
  cargo: ReadonlySet<string>
  /** H6: a system's display name, or null when unknown (`names.ts systemNameIn`); absent: rows name no system. */
  systemName?: SystemNameFn
}

/** A system id → its display name, or null when unknown. */
export type SystemNameFn = (systemId: string) => string | null

/** A raw system, station or ship id ("sys-05", "stn-12", "ship-7"): never shown to the player (D-TBD-17). */
export const RAW_ID_PATTERN = /\b(sys|stn|ship)-\d+/

/** `systemId`'s display name, or null when there is no lookup, the id is unknown, or the "name" is itself a raw id. */
export function wireSystemName(systemId: string, systemName: SystemNameFn | undefined): string | null {
  const name = systemName?.(systemId) ?? null
  return name === null || name.length === 0 || RAW_ID_PATTERN.test(name) ? null : name
}

/** Lower case, punctuation and runs of space folded to one space: the cause dedupe's compare form. */
function normalized(text: string): string {
  return text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
}

/** The item's cause line, or null when it has none or the headline already contains it (H6: no repeated cause). */
export function causeLine(item: WireItem): string | null {
  const cause = item.cause?.text ?? null
  if (cause === null) return null
  const c = normalized(cause)
  return c.length > 0 && normalized(item.headline).includes(c) ? null : cause
}

/**
 * True when `item` passes `filter`. "Nearby" is the player's system and its neighbours. "Disabled hulls" (M9) keeps
 * the CH-14b notices naming a disabled hull (a claimable hulk), told by their `wire-m9.json` headline.
 */
export function wireMatches(item: WireItem, filter: WireFilter, ctx: WireFilterContext): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'system':
      return ctx.systemId !== null && item.systemId === ctx.systemId
    case 'nearby':
      return (ctx.systemId !== null && item.systemId === ctx.systemId) || ctx.neighbours.has(item.systemId)
    case 'cargo':
      return item.ware !== undefined && ctx.cargo.has(item.ware)
    case 'hulls':
      return item.kind === 'notice' && isDisabledHullHeadline(item.headline)
  }
}

/** The items passing `filter`, newest first. */
export function wireFilters(items: readonly WireItem[], filter: WireFilter, ctx: WireFilterContext): WireItem[] {
  const out: WireItem[] = []
  for (let i = items.length - 1; i >= 0; i--) if (wireMatches(items[i], filter, ctx)) out.push(items[i])
  return out
}

/** The neighbours of `systemId` over the galaxy's relays. */
export function neighboursOf(relays: readonly { a: string; b: string }[], systemId: string | null): Set<string> {
  const out = new Set<string>()
  if (systemId === null) return out
  for (const r of relays) {
    if (r.a === systemId) out.add(r.b)
    else if (r.b === systemId) out.add(r.a)
  }
  return out
}

export const SEVERITY_LABELS: Readonly<Record<1 | 2 | 3, string>> = Object.freeze({ 1: 'Minor', 2: 'Notable', 3: 'Major' })
export const SEVERITY_GLYPHS: Readonly<Record<1 | 2 | 3, string>> = Object.freeze({ 1: '●', 2: '▲', 3: '▲▲' })

export interface WireRow {
  id: number
  /** For the map focus only; never shown (D-TBD-17). */
  systemId: string
  /** H6: the system's display name ("Vaenvyl"), or null when unknown (then the row names no system). */
  system: string | null
  /** "Day 12". */
  dayLabel: string
  severity: 1 | 2 | 3
  severityLabel: string
  severityGlyph: string
  headline: string
  /** The cause phrase, or null (also when the headline already says it). */
  cause: string | null
  /** M7 S39: the outlet that ran a story item (`wireOutlets.json`), or null for an ordinary item. */
  outlet: string | null
}

/** M7 S39: the display label of each story Wire outlet (spec §8.4, §12.3: every story item carries one). */
export const WIRE_OUTLET_LABELS: Readonly<Record<StoryWireOutlet, string>> = Object.freeze(outletsJson.labels)

/** A row without a system name lookup (the away report's headlines). */
export function wireRow(item: WireItem): WireRow {
  return wireRowNamed(item, undefined)
}

/** A row naming its system through `systemName`. */
export function wireRowNamed(item: WireItem, systemName: SystemNameFn | undefined): WireRow {
  return {
    id: item.id,
    systemId: item.systemId,
    system: wireSystemName(item.systemId, systemName),
    dayLabel: `Day ${Math.max(0, Math.floor(item.day))}`,
    severity: item.severity,
    severityLabel: SEVERITY_LABELS[item.severity],
    severityGlyph: SEVERITY_GLYPHS[item.severity],
    headline: item.headline,
    cause: causeLine(item),
    outlet: item.source === undefined ? null : WIRE_OUTLET_LABELS[item.source],
  }
}

export interface WirePanelModel {
  filter: WireFilter
  rows: WireRow[]
  /** Shown when no row passes. */
  empty: string | null
}

const EMPTY: Readonly<Record<WireFilter, string>> = Object.freeze({
  all: 'No news on the Wire yet.',
  system: 'Nothing on the Wire about this system.',
  nearby: 'Nothing on the Wire from nearby systems.',
  cargo: 'Nothing on the Wire about the wares in your hold.',
  hulls: 'No disabled hulls on the Wire.',
})

export function wirePanelModel(items: readonly WireItem[], filter: WireFilter, ctx: WireFilterContext): WirePanelModel {
  const rows = wireFilters(items, filter, ctx).map((item) => wireRowNamed(item, ctx.systemName))
  return { filter, rows, empty: rows.length === 0 ? EMPTY[filter] : null }
}

/** The next filter left (-1) or right (+1), wrapping. */
export function stepFilter(filter: WireFilter, dir: -1 | 1): WireFilter {
  const n = WIRE_FILTERS.length
  return WIRE_FILTERS[(WIRE_FILTERS.indexOf(filter) + dir + n) % n]
}
